import { decamelize } from 'humps'

// each endpoint caps its page size (~1000, sometimes less); stay safely below it
const PAGE_SIZE = 250
// ids go on the query string (?_id=...&_id=...), so keep each request well under
// typical URL/header limits — ~100 ObjectIds is roughly 3KB
const BY_ID_PAGE_SIZE = 100
// fallback for collections that can only be fetched one id at a time
const BY_ID_CONCURRENCY = 10

const projectAudience = (collection, audience) =>
  Object.fromEntries(
    collection.projection
      .filter((field) => field in audience)
      .map((field) => [field, audience[field]])
  )

/**
 * Loads every page matching the query context into the client.
 * @param {object} collection - a COLLECTIONS entry (see collections.js)
 * @param {object} context - collection-specific query context (filters, taxonomy, ...)
 * @param {function} onProgress - called with { loaded, total } after each page
 */
export async function fetchAllAudiences(collection, context, onProgress = () => {}) {
  const fields = collection.projection.map((field) => decamelize(field))
  const items = []
  let skip = 0
  let count = Infinity

  while (skip < count) {
    const response = await collection.fetchPage(context, {
      fields,
      limit: PAGE_SIZE,
      skip,
      sort: collection.defaultSort,
    })
    count = response.count ?? 0
    items.push(...response.items.map((audience) => projectAudience(collection, audience)))
    skip += PAGE_SIZE
    onProgress({ loaded: items.length, total: count })
    if (!response.items.length) break
  }

  return items
}

/**
 * Loads audiences from a known list of ids (used to reload/retry past sessions,
 * where re-running the original filters could return already-updated audiences).
 * Uses the collection's bulk list endpoint when available (one request per page,
 * as fast as the original query) and falls back to per-id requests otherwise.
 * Returns { audiences, missing } so callers can surface ids that no longer resolve.
 */
export async function fetchAudiencesByIds(collection, ids, onProgress = () => {}) {
  if (collection.fetchByIds) {
    return fetchByIdsPaged(collection, ids, onProgress)
  }
  return fetchByIdsIndividually(collection, ids, onProgress)
}

async function fetchByIdsPaged(collection, ids, onProgress) {
  const fields = collection.projection.map((field) => decamelize(field))
  const requested = new Set(ids)
  const audiences = []
  const found = new Set()

  for (let start = 0; start < ids.length; start += BY_ID_PAGE_SIZE) {
    const chunk = ids.slice(start, start + BY_ID_PAGE_SIZE)
    const response = await collection.fetchByIds(chunk, { fields, limit: chunk.length })
    ;(response.items ?? []).forEach((audience) => {
      // only keep what we asked for, in case the endpoint ignores the id filter
      if (requested.has(audience._id) && !found.has(audience._id)) {
        audiences.push(projectAudience(collection, audience))
        found.add(audience._id)
      }
    })
    onProgress({ loaded: Math.min(start + chunk.length, ids.length), total: ids.length })
  }

  // anything the bulk query didn't return (dropped by a default list filter, a
  // server-side page cap, etc.) is confirmed one by one before being reported as
  // missing — so existing audiences are never falsely flagged as "could not load"
  const notReturned = ids.filter((id) => !found.has(id))
  if (notReturned.length === 0) {
    return { audiences, missing: [] }
  }

  const fallback = await fetchByIdsIndividually(collection, notReturned, onProgress)
  return { audiences: [...audiences, ...fallback.audiences], missing: fallback.missing }
}

async function fetchByIdsIndividually(collection, ids, onProgress) {
  const audiences = []
  const missing = []

  for (let start = 0; start < ids.length; start += BY_ID_CONCURRENCY) {
    const batch = ids.slice(start, start + BY_ID_CONCURRENCY)
    const responses = await Promise.all(
      batch.map((id) => collection.fetchById(id).catch(() => null))
    )
    responses.forEach((audience, index) => {
      if (audience?._id) {
        audiences.push(projectAudience(collection, audience))
      } else {
        missing.push(batch[index])
      }
    })
    onProgress({ loaded: audiences.length + missing.length, total: ids.length })
  }

  return { audiences, missing }
}
